/**
 * Morning Touch-Base Settings Drawer
 * Mission 32: Cron Orchestration & Scheduled Tasks
 * 
 * Purpose: Configure morning check-in nudge preferences
 * 
 * Settings:
 * - Morning nudge is ALWAYS ON (no toggle — it's a daily chat message)
 * - Time preference (device-dependent):
 *   - WITH device: Minutes after wake (10-120 min)
 *   - WITHOUT device: Absolute time (7:00 AM)
 * - Device detection: Auto-detected from Garmin/Apple Watch connections
 * 
 * Created: February 2, 2026
 */

import React, { useState, useEffect } from 'react';
import { X, Clock, Watch, Sunrise } from 'lucide-react';
import './MorningTouchBaseDrawer.css';

interface MorningTouchBaseSettings {
  has_watch: boolean;  // Auto-detected from user_core_profile.has_fitness_device
  preferred_time_after_wake_minutes: number;  // For users WITH watch
  preferred_absolute_time: string;  // For users WITHOUT watch (format: "07:00")
  earliest_morning_time: string;  // For users WITH watch: don't nudge before this time (format: "06:00")
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
}

export function MorningTouchBaseDrawer({ isOpen, onClose, userId }: Props) {
  const [settings, setSettings] = useState<MorningTouchBaseSettings>({
    has_watch: false,
    preferred_time_after_wake_minutes: 10,
    preferred_absolute_time: '07:00',
    earliest_morning_time: '06:00'
  });
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Load current settings
  useEffect(() => {
    if (isOpen) {
      loadSettings();
    }
  }, [isOpen]);

  const loadSettings = async () => {
    try {
      setLoading(true);
      
      const response = await fetch(`/api/users/${userId}/nudge-preferences`);
      const data = await response.json();
      
      if (data.nudge_pref?.morning_checkin) {
        setSettings({
          has_watch: data.nudge_pref.morning_checkin.has_watch ?? false,
          preferred_time_after_wake_minutes: data.nudge_pref.morning_checkin.preferred_time_after_wake_minutes ?? 10,
          preferred_absolute_time: data.nudge_pref.morning_checkin.preferred_absolute_time ?? '07:00',
          earliest_morning_time: data.nudge_pref.morning_checkin.earliest_morning_time ?? '06:00'
        });
      }
      
    } catch (error) {
      console.error('Failed to load morning touch-base settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      // Send timing preferences only; has_watch is auto-detected on backend
      const payload: Record<string, unknown> = {
        preferred_absolute_time: settings.preferred_absolute_time,
      };
      if (settings.has_watch) {
        payload.preferred_time_after_wake_minutes = settings.preferred_time_after_wake_minutes;
        payload.earliest_morning_time = settings.earliest_morning_time;
      }
      const response = await fetch(`/api/users/${userId}/nudge-preferences`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          morning_checkin: payload
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to save settings');
      }
      
      onClose();
      
    } catch (error) {
      console.error('Failed to save morning touch-base settings:', error);
      alert('Failed to save settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end sm:items-center justify-center">
      <div className="bg-white w-full sm:max-w-md sm:rounded-lg shadow-xl max-h-[80vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Sunrise className="w-6 h-6 text-cedar-primary" />
            <h2 className="text-lg font-semibold text-gray-900">Morning Touch-Base</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {loading ? (
          <div className="p-6 flex justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cedar-primary"></div>
          </div>
        ) : (
          <div className="p-6 space-y-6">
            {/* Header — morning nudge is always on */}
            <div>
              <p className="text-sm text-gray-500">
                Cedar checks in every morning to preview your day. Set your preferred time below.
              </p>
            </div>

                {/* Sleep Tracking Status */}
                {settings.has_watch ? (
                  <div className="bg-emerald-50 rounded-lg p-4 border border-emerald-200">
                    <div className="flex items-center gap-2">
                      <Watch className="w-5 h-5 text-emerald-600" />
                      <span className="text-sm font-medium text-emerald-900">
                        Sleep Tracking Active
                      </span>
                    </div>
                    <p className="text-sm text-emerald-700 mt-2">
                      Cedar detects when you wake up automatically
                    </p>
                  </div>
                ) : (
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <div className="flex items-center gap-2">
                      <Sunrise className="w-5 h-5 text-gray-600" />
                      <span className="text-sm font-medium text-gray-900">
                        No Sleep Tracking
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mt-2">
                      Set your preferred wake time below, or connect a device for automatic detection
                    </p>
                  </div>
                )}

                {/* Time Preference (Conditional on has_watch) */}
                {settings.has_watch ? (
                  // WITH SLEEP TRACKING: Minutes after wake
                  <div>
                    <label className="block text-sm font-medium text-gray-900 mb-2">
                      <Clock className="w-4 h-4 inline mr-2" />
                      Check in after waking up
                    </label>
                    <input
                      type="range"
                      min="10"
                      max="60"
                      step="5"
                      value={settings.preferred_time_after_wake_minutes}
                      onChange={(e) => setSettings(prev => ({
                        ...prev,
                        preferred_time_after_wake_minutes: parseInt(e.target.value)
                      }))}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-cedar-primary"
                    />
                    <div className="flex justify-between mt-2">
                      <span className="text-sm text-gray-600">10 min</span>
                      <span className="text-base font-semibold text-cedar-primary">
                        {settings.preferred_time_after_wake_minutes} minutes
                      </span>
                      <span className="text-sm text-gray-600">60 min</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      Cedar will reach out {settings.preferred_time_after_wake_minutes} minutes after you wake up
                    </p>

                    {/* Earliest morning time — prevents nighttime bathroom trip nudges */}
                    <label className="block text-sm font-medium text-gray-900 mb-2 mt-6">
                      <Clock className="w-4 h-4 inline mr-2" />
                      Don't nudge before
                    </label>
                    <input
                      type="time"
                      value={settings.earliest_morning_time}
                      onChange={(e) => setSettings(prev => ({
                        ...prev,
                        earliest_morning_time: e.target.value
                      }))}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cedar-primary focus:border-transparent"
                    />
                    <p className="text-xs text-gray-500 mt-2">
                      Even if your watch detects you're awake, Cedar won't check in before this time
                    </p>
                  </div>
                ) : (
                  // WITHOUT SLEEP TRACKING: Absolute time
                  <div>
                    <label className="block text-sm font-medium text-gray-900 mb-2">
                      <Clock className="w-4 h-4 inline mr-2" />
                      Preferred Morning Time
                    </label>
                    <input
                      type="time"
                      value={settings.preferred_absolute_time}
                      onChange={(e) => setSettings(prev => ({
                        ...prev,
                        preferred_absolute_time: e.target.value
                      }))}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cedar-primary focus:border-transparent"
                    />
                    <p className="text-xs text-gray-500 mt-2">
                      Cedar will check in around this time each morning
                    </p>
                  </div>
                )}

                {/* Info Box */}
                <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                  <h4 className="text-sm font-medium text-blue-900 mb-2">
                    What to expect
                  </h4>
                  <ul className="text-sm text-blue-800 space-y-1">
                    <li>{settings.has_watch ? '• Quick energy check based on your sleep' : '• Quick energy check ("How\'d you sleep?")'}</li>
                    <li>• Preview of today's workout</li>
                    <li>• Weather awareness for outdoor sessions</li>
                    <li>• Flexibility if you need it</li>
                  </ul>
                </div>
          </div>
        )}

        {/* Footer Actions */}
        <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="morning-drawer-save-btn flex-1 px-4 py-2 text-white rounded-lg transition-colors disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
